import React, { Component } from 'react';

import { get } from '../utils/apiClient';
import { uploadEventPoster } from '../utils/imageApi';
import { WEEKDAYS, formatStartTime } from '../utils/seriesApi';

// Alta y edición de una serie del club (FR-001, FR-002).
//
// Overlay propio y no modal de Bootstrap (el backdrop de Bootstrap sobrevive a la navegación y
// deja la pantalla siguiente congelada). Molde: MembershipSellModal; el póster es el de
// FormEventComponent (sube por el backend con `uploadEventPoster`, que le pone el nombre al
// objeto y valida tipo y tamaño).
//
// ⚠️ El casing NO es simétrico y es lo que más rompe acá: la serie LLEGA con las columnas en
// snake_case (`branch_id`, `start_time`, `poster_url`) y se MANDA en camelCase (`branchId`,
// `startTime`, `posterUrl`). Leer `initial.startTime` da `undefined` y deja la hora vacía al
// editar, que es un campo obligatorio: la serie se guardaría con "Hora inválida".
class FormSeriesComponent extends Component {

    constructor(props) {
        super(props);
        const initial = props.initial || {};
        // 'HH:mm' es lo que quiere <input type="time">; `formatStartTime` devuelve '—' cuando
        // no puede leer la hora, y ese guión NO puede terminar en el body (el backend lo
        // rechazaría con "Hora inválida"): se traduce a campo vacío, que sí se ve.
        const hora = formatStartTime(initial.start_time != null ? initial.start_time : initial.startTime);
        this.state = {
            name: initial.name || '',
            // snake_case con fallback a camel, mismo criterio que FormEventComponent.
            branchId: (initial.branch_id != null ? initial.branch_id : initial.branchId) != null
                ? String(initial.branch_id != null ? initial.branch_id : initial.branchId)
                : '',
            weekday: initial.weekday != null ? String(initial.weekday) : '2',
            // `start_time` es una columna TIME ('20:00:00') y <input type="time"> quiere 'HH:mm'.
            startTime: hora === '—' ? '' : hora,
            visibility: initial.visibility || 'private',
            description: initial.description || '',
            notes: initial.notes || '',
            active: initial.active === undefined ? true : !!initial.active,
            posterUrl: (initial.poster_url != null ? initial.poster_url : initial.posterUrl) || '',
            posterFile: null,
            posterPreview: (initial.poster_url != null ? initial.poster_url : initial.posterUrl) || '',
            branches: [],
            uploading: false,
            saving: false,
            error: null,
        };
    }

    componentDidMount() {
        this.loadBranches();
    }

    loadBranches = async () => {
        try {
            const branches = (await get('branch')) || [];
            this.setState((prev) => ({
                branches,
                branchId: prev.branchId || (branches[0] ? String(branches[0].id) : ''),
            }));
        } catch (e) {
            this.setState({ error: e.message });
        }
    }

    campo = (k, v) => this.setState({ [k]: v, error: null })

    handlePosterChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        this.setState({ posterFile: file, posterPreview: URL.createObjectURL(file), error: null });
    }

    // Sube el póster pendiente y devuelve la URL (o null si falla). Limpia `posterFile` en
    // éxito para que deje de considerarse pendiente.
    uploadPoster = async () => {
        if (!this.state.posterFile) return null;
        this.setState({ uploading: true, error: null });
        try {
            const subido = await uploadEventPoster(this.state.posterFile, this.state.posterFile.name);
            this.setState({ posterUrl: subido.url, posterPreview: subido.url, posterFile: null, uploading: false });
            return subido.url;
        } catch (e) {
            // El motivo lo da el backend ("La imagen pesa X MB…"), que es lo accionable.
            this.setState({ uploading: false, error: `No se pudo subir el póster: ${e.message}` });
            return null;
        }
    }

    onClickUploadPoster = async () => {
        const url = await this.uploadPoster();
        if (url) this.setState({ error: null });
    }

    // Todo en camelCase: es lo que acepta el body de POST/PATCH.
    //
    // El póster vacío NO se manda: la columna es NOT NULL y el backend rechaza un `posterUrl`
    // presente y vacío con "El póster es obligatorio". En edición, no mandarlo es "dejalo como
    // está" — quitarle el póster a una serie no es una operación que exista.
    buildPayload = (posterUrl) => {
        const payload = {
            name: this.state.name.trim(),
            branchId: parseInt(this.state.branchId, 10),
            weekday: Number(this.state.weekday),
            startTime: this.state.startTime,
            visibility: this.state.visibility,
            // Vacío limpia la columna: el backend hace `body.description || null`.
            description: this.state.description.trim() || null,
            notes: this.state.notes.trim() || null,
            active: !!this.state.active,
        };
        if (posterUrl) payload.posterUrl = posterUrl;
        return payload;
    }

    guardar = async () => {
        if (this.state.saving || this.state.uploading) return;
        const editMode = !!this.props.editMode;
        if (!this.state.name.trim()) { this.setState({ error: 'El nombre de la serie es obligatorio.' }); return; }
        if (!String(this.state.branchId).trim()) { this.setState({ error: 'Elegí la sucursal.' }); return; }
        if (!this.state.startTime) { this.setState({ error: 'Poné la hora de la jornada (HH:mm).' }); return; }
        // ENI-155 del lado de la serie: el póster es obligatorio porque la primera jornada lo
        // hereda y `createEvent` lo exige. Mismo gate que FormEventComponent.
        if (!editMode && !this.state.posterUrl && !this.state.posterFile) {
            this.setState({ error: 'Subí un póster antes de crear la serie: la primera jornada lo hereda.' });
            return;
        }
        // Si hay un póster elegido y todavía no subido, se sube ahora: descartarlo en silencio
        // es peor que tardar dos segundos.
        let posterUrl = this.state.posterUrl;
        if (this.state.posterFile) {
            posterUrl = await this.uploadPoster();
            if (!posterUrl) return;
        }
        this.setState({ saving: true, error: null });
        try {
            await this.props.onSave(this.buildPayload(posterUrl));
        } catch (e) {
            this.setState({ error: e.message, saving: false });
        }
    }

    render() {
        const editMode = !!this.props.editMode;
        return (
            <div style={{
                position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(15,23,42,.45)',
                zIndex: 1080, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                padding: '6vh 16px', overflowY: 'auto',
            }} onClick={this.props.onCancel}>
                <div className="card card-small" style={{ width: 620, maxWidth: '100%' }}
                    onClick={(e) => e.stopPropagation()}>
                    <div className="card-header border-bottom d-flex justify-content-between align-items-center">
                        <h6 className="m-0">{editMode ? 'Editar serie' : 'Nueva serie del club'}</h6>
                        <button type="button" className="btn btn-sm btn-white" onClick={this.props.onCancel}>Cancelar</button>
                    </div>
                    <div className="card-body">
                        <div className="row">
                            <div className="col-md-7 form-group">
                                <label>Nombre *</label>
                                <input type="text" className="form-control" autoFocus value={this.state.name}
                                    placeholder="Club de los martes"
                                    onChange={(e) => this.campo('name', e.target.value)} />
                                <small className="text-muted">Cada jornada nace titulada "{this.state.name.trim() || 'Nombre de la serie'} · dd/mm".</small>
                            </div>
                            <div className="col-md-5 form-group">
                                <label>Sucursal *</label>
                                <select className="form-control" value={this.state.branchId}
                                    onChange={(e) => this.campo('branchId', e.target.value)}>
                                    <option value="">Seleccionar…</option>
                                    {this.state.branches.map((b) => (
                                        <option key={b.id} value={b.id}>{b.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-4 form-group">
                                <label>Día *</label>
                                <select className="form-control" value={this.state.weekday}
                                    onChange={(e) => this.campo('weekday', e.target.value)}>
                                    {WEEKDAYS.map((d) => (
                                        <option key={d.value} value={d.value}>{d.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-3 form-group">
                                <label>Hora *</label>
                                <input type="time" className="form-control" value={this.state.startTime}
                                    onChange={(e) => this.campo('startTime', e.target.value)} />
                            </div>
                            <div className="col-md-5 form-group">
                                <label>Visibilidad</label>
                                <select className="form-control" value={this.state.visibility}
                                    onChange={(e) => this.campo('visibility', e.target.value)}>
                                    <option value="private">Privada (no se lista en la web)</option>
                                    <option value="public">Pública (se lista en la web)</option>
                                </select>
                                <small className="text-muted">La jornada hereda esto de la serie: privada se vende igual en el mostrador y por link.</small>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Descripción</label>
                            <textarea className="form-control" rows="2" value={this.state.description}
                                onChange={(e) => this.campo('description', e.target.value)} />
                            <small className="text-muted">La hereda cada jornada nueva. Se puede cambiar después en la jornada.</small>
                        </div>

                        <div className="form-group">
                            <label>Notas internas</label>
                            <textarea className="form-control" rows="2" value={this.state.notes}
                                onChange={(e) => this.campo('notes', e.target.value)} />
                            <small className="text-muted">Sólo las ve el equipo: nunca salen por la web.</small>
                        </div>

                        <div className="form-group">
                            <label>
                                <input type="checkbox" checked={this.state.active}
                                    onChange={(e) => this.setState({ active: e.target.checked, error: null })} />
                                {' '}Serie activa
                            </label>
                            <small className="d-block text-muted">Una serie inactiva conserva su historial y deja de ofrecer "Nueva jornada".</small>
                        </div>

                        <hr />
                        <h6 className="text-uppercase text-muted">{editMode ? 'Póster' : 'Póster *'}</h6>
                        <div className="form-row">
                            <div className="form-group col-md-6">
                                {this.state.posterPreview && (
                                    <div className="mb-2">
                                        <img src={this.state.posterPreview} alt="Vista previa del póster" width="120" />
                                    </div>
                                )}
                                <input type="file" accept="image/*" onChange={this.handlePosterChange} />
                                <div className="mt-2">
                                    <button type="button" className="btn btn-outline-secondary btn-sm"
                                        disabled={this.state.uploading || !this.state.posterFile}
                                        onClick={this.onClickUploadPoster}>
                                        {this.state.uploading ? 'Subiendo…' : 'Subir póster'}
                                    </button>
                                </div>
                                <small className="d-block text-muted mt-2">
                                    Lo hereda cada jornada nueva. Si elegís una imagen y no la subís, se sube al guardar.
                                </small>
                            </div>
                        </div>

                        {this.state.error && (
                            <div className="alert alert-danger py-2" role="alert">{this.state.error}</div>
                        )}

                        <button type="button" className="btn btn-accent"
                            disabled={this.state.saving || this.state.uploading} onClick={this.guardar}>
                            {this.state.saving ? 'Guardando…' : (editMode ? 'Guardar cambios' : 'Crear serie')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default FormSeriesComponent;
